"use client";

/**
 * Embed ufficiale Iubenda per Privacy Policy / Cookie Policy.
 *
 * <p>Renderizza l'esatto snippet "raccomandato" da Iubenda:</p>
 * <pre>{@code
 *   <a class="iubenda-white iubenda-noiframe iubenda-embed">Privacy Policy</a>
 *   <script src="https://cdn.iubenda.com/iubenda.js" />
 * }</pre>
 *
 * <p>Differenze rispetto al copy-paste manuale dello snippet Iubenda:</p>
 * <ol>
 *   <li><b>Script via {@link next/script}</b> con strategy "afterInteractive":
 *       Next.js carica lo script una sola volta per sessione, in ordine
 *       deterministico, dopo l'hydration. Lo snippet IIFE originale di
 *       Iubenda usa {@code window.onload} che in una SPA Next.js si
 *       attiva solo al primo navigation (i page change client-side non
 *       lo riemettono).</li>
 *   <li><b>Re-trigger su navigazione</b>: quando l'utente naviga
 *       client-side (es. dal footer alla pagina privacy), lo script Iubenda
 *       e' gia' caricato ma non si "ri-attiva" sui nuovi link. Chiamiamo
 *       manualmente {@code _iub.csConfiguration} o ri-eseguiamo lo
 *       scan via la global {@code window._iub.fragments?.reload?.()}
 *       quando disponibile.</li>
 *   <li><b>Variante "inline"</b>: oltre al link-popup standard, esponiamo
 *       una variante {@code inline} che embedda l'iframe full-legal nella
 *       pagina (UX migliore sulle pagine dedicate /privacy e /cookie:
 *       l'utente vede subito il testo invece di dover cliccare).</li>
 * </ol>
 *
 * <p><b>Policy ID</b>: hardcoded 37648532 come fornito. Per portabilita',
 * sovrascrivibile via env {@code NEXT_PUBLIC_IUBENDA_POLICY_ID} oppure
 * via prop esplicita {@code policyId}.</p>
 */

import Script from "next/script";
import { useEffect } from "react";

const DEFAULT_POLICY_ID =
  process.env.NEXT_PUBLIC_IUBENDA_POLICY_ID || "37648532";

export type IubendaPolicyKind = "privacy" | "cookie";

interface IubendaPolicyEmbedProps {
  /** Tipo policy: "privacy" (Privacy Policy) o "cookie" (Cookie Policy). */
  kind: IubendaPolicyKind;
  /**
   * Variante di rendering:
   *   - "link": solo il link cliccabile (apre modal Iubenda al click) —
   *     adatto al footer o a menzioni inline.
   *   - "inline": iframe della policy embedded direttamente nella pagina —
   *     adatto a {@code /privacy} e {@code /cookie} (UX migliore, testo
   *     subito visibile, SEO friendly).
   * Default: "inline".
   */
  variant?: "link" | "inline";
  /** Override del policy ID (default: env var o fallback hardcoded). */
  policyId?: string;
  /** Etichetta del link (solo per variant="link"). */
  label?: string;
  /** Classe Tailwind/CSS aggiuntiva applicata al wrapper. */
  className?: string;
}

/** Costruisce l'URL pubblico Iubenda per la policy data. */
function policyUrl(policyId: string, kind: IubendaPolicyKind): string {
  const base = `https://www.iubenda.com/privacy-policy/${policyId}`;
  return kind === "cookie" ? `${base}/cookie-policy` : base;
}

export default function IubendaPolicyEmbed({
  kind,
  variant = "inline",
  policyId = DEFAULT_POLICY_ID,
  label,
  className,
}: IubendaPolicyEmbedProps) {
  const url = policyUrl(policyId, kind);
  const defaultLabel =
    label ?? (kind === "privacy" ? "Privacy Policy" : "Cookie Policy");

  // Re-trigger dello scanner Iubenda sui navigation client-side: la global
  // window._iub e' presente dopo il primo load dello script CDN; se non
  // c'e' (es. SSR o script ancora in fetch), no-op silenzioso.
  useEffect(() => {
    if (variant !== "link") return;
    if (typeof window === "undefined") return;
    type IubGlobal = {
      _iub?: {
        fragments?: { reload?: () => void };
      };
    };
    const w = window as IubGlobal & typeof globalThis;
    w._iub?.fragments?.reload?.();
  }, [variant, kind, policyId]);

  if (variant === "link") {
    return (
      <>
        <a
          href={url}
          className={`iubenda-white iubenda-noiframe iubenda-embed${className ? " " + className : ""}`}
          title={defaultLabel}
        >
          {defaultLabel}
        </a>
        <Script
          id={`iubenda-loader-${kind}`}
          src="https://cdn.iubenda.com/iubenda.js"
          strategy="afterInteractive"
        />
      </>
    );
  }

  // variant === "inline": embed UFFICIALE Iubenda (iubenda.js + class
  // "iubenda-noiframe iubenda-embed"). NON usa un <iframe> diretto verso
  // www.iubenda.com: quell'approccio veniva RIFIUTATO da Iubenda
  // (X-Frame-Options → "Connessione negata"). Il loader iubenda.js apre il
  // documento completo in un overlay HTML servito da Iubenda (frame-safe).
  // Mostriamo un CTA evidente + il link diretto in nuova scheda come fallback
  // sempre funzionante.
  return (
    <div className={className}>
      <div className="border border-neutral-200 rounded-lg bg-white p-6 sm:p-8 text-center">
        <p className="text-neutral-600 mb-4">
          L&apos;informativa completa è gestita e mantenuta aggiornata su Iubenda.
        </p>
        <a
          href={url}
          className="iubenda-white iubenda-noiframe iubenda-embed inline-flex items-center justify-center rounded-md bg-[#1a3a5c] px-6 py-3 text-sm font-semibold text-white hover:bg-[#2a5a8c] transition-colors"
          title={defaultLabel}
        >
          Visualizza la {defaultLabel}
        </a>
        <Script
          id={`iubenda-loader-${kind}`}
          src="https://cdn.iubenda.com/iubenda.js"
          strategy="afterInteractive"
        />
        <p className="mt-4 text-xs text-neutral-500">
          Oppure apri il documento in una{" "}
          <a
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            className="text-[#4a90c4] hover:underline"
          >
            nuova scheda
          </a>
          .
        </p>
      </div>
    </div>
  );
}
